import { useSyncExternalStore } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

/**
 * Unified connectivity detection service.
 * - Native (Capacitor): Uses @capacitor/network plugin for reliable detection
 * - Web (browser): Uses navigator.onLine + window online/offline events
 *
 * Exposes a React hook (useNetworkStatus) and imperative API (isOnline, onStatusChange).
 */

// ---- Module-level state ----
let _online = typeof navigator !== 'undefined' ? navigator.onLine : true;
let _connectionType = 'unknown';
const _listeners = new Set<() => void>();

function setOnlineState(online: boolean, connectionType?: string) {
  const changed = _online !== online;
  _online = online;
  if (connectionType !== undefined) _connectionType = connectionType;
  if (changed) {
    _listeners.forEach((cb) => cb());
  }
}

// ---- Initialisation (runs once on import) ----
function init() {
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    // Native: use Capacitor Network plugin
    Network.getStatus()
      .then((status) => {
        setOnlineState(status.connected, status.connectionType);
      })
      .catch(() => {
        // Fall back to navigator.onLine
        setOnlineState(navigator.onLine);
      });

    Network.addListener('networkStatusChange', (status) => {
      setOnlineState(status.connected, status.connectionType);
    });
  } else {
    // Web: use browser events
    _online = navigator.onLine;

    window.addEventListener('online', () => setOnlineState(true));
    window.addEventListener('offline', () => setOnlineState(false));
  }
}

init();

// ---- Public imperative API ----

/** Check current online/offline status */
export function isOnline(): boolean {
  return _online;
}

/** Get the connection type (wifi, cellular, none, unknown) */
export function getConnectionType(): string {
  return _connectionType;
}

/** Subscribe to connectivity changes. Returns an unsubscribe function. */
export function onStatusChange(callback: () => void): () => void {
  _listeners.add(callback);
  return () => {
    _listeners.delete(callback);
  };
}

// ---- React hook (useSyncExternalStore) ----

function subscribe(callback: () => void): () => void {
  _listeners.add(callback);
  return () => {
    _listeners.delete(callback);
  };
}

function getSnapshot(): boolean {
  return _online;
}

function getServerSnapshot(): boolean {
  return true; // SSR fallback — always assume online
}

/** React hook for network connectivity status */
export function useNetworkStatus(): { online: boolean; connectionType: string } {
  const online = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { online, connectionType: _connectionType };
}
